import type { DocId, NodeId } from './types';

/**
 * THE RIGHT-CLICK MENUS (V0.54) — what each menu offers, in which order, to whom.
 *
 * Five places answer a right-click: a node, the empty map, a dotted link, a documentation pill
 * and the minimap. Each item below was chosen, and put in its place, by the user from a proof
 * of concept; the arrangement string it produced is quoted in the V0.54 CHANGELOG entry. This
 * file IS that arrangement, so a change to what a menu offers is a change here and nowhere
 * else — the region draws what `buildMenu` returns and runs what is clicked.
 *
 * PURE: facts in, entries out. `features/contextmenu/ContextMenu.tsx` reads the live state
 * into a `MenuFacts` and hands it over, so every rule here — which items a person may use,
 * which apply to this node, what they are called — is a function of plain data and is tested
 * as one (`contextMenu.test.ts`).
 *
 * THREE RULES THE CHOSEN SHAPE FIXES:
 *  - An item a person MAY NOT use is never drawn — the rule every toolbar button follows.
 *  - An item that does not APPLY here is not drawn either ("hide", not "grey out"): Paste
 *    with nothing copied, Expand on a leaf, a page menu for a node with no pages.
 *  - Groups are separated by a line; there are no group headings.
 *
 * The task items appear on PROJECT nodes only (the user's amendment to the arrangement): a
 * task can live on any node, but "Show tasks" on every node of the map was noise, and a
 * project is where somebody goes looking for the work.
 */

export type MenuTarget =
  | { kind: 'node'; nodeId: NodeId }
  /** The world point that was clicked — where "Add a node here" and "Paste here" land. */
  | { kind: 'map'; x: number; y: number }
  | { kind: 'link'; index: number }
  | { kind: 'pill'; nodeId: NodeId; docId: DocId }
  | { kind: 'minimap' };

export type ItemId =
  // a node
  | 'open' | 'expand' | 'expandBeneath' | 'focus' | 'frame' | 'copyLink' | 'favourite' | 'follow'
  | 'tasks' | 'addTask' | 'addChild' | 'addSibling' | 'rename' | 'colour' | 'link' | 'move'
  | 'copy' | 'copyBranch' | 'duplicate' | 'paste' | 'createDoc' | 'editDoc' | 'tags' | 'undo'
  | 'delete'
  // the empty map
  | 'addHere' | 'pasteHere' | 'fit' | 'expandAll' | 'collapseAll' | 'showLinks' | 'docsPinned'
  | 'heatmap' | 'minimap' | 'clearFilters' | 'realign' | 'editMode'
  // a dotted link
  | 'goToA' | 'goToB' | 'linkColour' | 'removeLink' | 'hideLinks'
  // a documentation pill
  | 'openDoc' | 'openDocNewTab' | 'copyDocLink' | 'editDocPage'
  // the minimap
  | 'minimapFit' | 'minimapBright' | 'minimapHide';

/** One page this node can show or edit, or one slot it could fill, for a submenu row. */
export interface DocChoice {
  /** The doc id to open or edit, or the slot's permission key to create. */
  arg: string;
  label: string;
  /** A CSS colour — the kind's dot. */
  color: string;
}

export interface NodeFacts {
  id: NodeId;
  label: string;
  color: string;
  root: boolean;
  collapsed: boolean;
  hasChildren: boolean;
  /** How many nodes are beneath it (visible to this user), for the branch items' labels. */
  descendantCount: number;
  /** Some node in this branch (itself included) is folded and has something to show. */
  foldedBeneath: boolean;
  isProject: boolean;
  /** null while this user's favourites are not yet known — the item waits rather than guesses. */
  favourite: boolean | null;
  /** null while this user's subscriptions are not yet known. */
  following: boolean | null;
  /** The fixed slots this user may create a page in, and that are empty. */
  missingKinds: readonly DocChoice[];
  /** The pages on this node this user may edit in the current language. */
  editableDocs: readonly DocChoice[];
  /** The clipboard's node may be pasted under this one (never into its own branch). */
  pasteOk: boolean;
}

export interface MenuFacts {
  editing: boolean;
  may: {
    enterEdit: boolean;
    editMap: boolean;
    editLinks: boolean;
    assignTags: boolean;
    viewTodos: boolean;
    editTodos: boolean;
  };
  node?: NodeFacts;
  link?: { aLabel: string; bLabel: string; color: string };
  pill?: { kindLabel: string; nodeLabel: string; color: string; canEdit: boolean };
  map: {
    anyFolded: boolean;
    anyOpen: boolean;
    showLinks: boolean;
    docsPinned: boolean;
    heatmapOn: boolean;
    minimapOn: boolean;
    filterActive: boolean;
    /** Realign would tidy the WHOLE tree — false while branches are withheld from this user. */
    realignOk: boolean;
  };
  minimapBright: boolean;
  clipboard: { label: string; count: number } | null;
  undoLabel: string | null;
}

export interface MenuItem {
  type: 'item';
  id: ItemId;
  label: string;
  /** An icon symbol in `index.html`, without the `#`. */
  icon: string;
  /** The gesture that does the same thing — shown beside the label as a hint. */
  keys?: string;
  /** A tick-box row: the tick says whether the thing is on. */
  checked?: boolean;
  danger?: boolean;
  /** What the item acts on, when the id alone does not say: a slot, a doc id. */
  arg?: string;
  /** A submenu. Each row carries the parent's `id` and its own `arg`. */
  sub?: readonly MenuSubItem[];
  /** A colour submenu, opened on this colour. */
  swatches?: string;
}

export interface MenuSubItem {
  label: string;
  arg: string;
  /** The kind's dot, drawn where the icon would be. */
  dot?: string;
}

export type MenuEntry = MenuItem | { type: 'separator' };

/** Drawn above the items: the node chip, or a plain title where there is no node to draw. */
export interface MenuHeader {
  label: string;
  /** A node-shaped chip in this colour; null for a plain title. */
  color: string | null;
  root: boolean;
}

export interface BuiltMenu {
  header: MenuHeader | null;
  entries: MenuEntry[];
}

/** Which of the two edit states an item is offered in. */
type Modes = 'view' | 'edit' | 'both';

interface Spec {
  id: ItemId;
  /** Items in the same group sit together; a line separates groups. */
  group: string;
  modes: Modes;
  /** Offered for these facts? Covers BOTH "may use" and "applies here". */
  show: (f: MenuFacts) => boolean;
  make: (f: MenuFacts) => MenuItem;
}

const item = (id: ItemId, label: string, icon: string, extra: Partial<MenuItem> = {}): MenuItem => ({
  type: 'item',
  id,
  label,
  icon,
  ...extra,
});

const quote = (s: string) => `“${s}”`;
/** "Paste “Jobs” here", or "Paste “Advanced Config” + 4 here" for a branch. */
const pasteLabel = (c: { label: string; count: number }) =>
  `Paste ${quote(c.label)}${c.count > 1 ? ` + ${c.count - 1}` : ''} here`;
const node = (f: MenuFacts) => f.node!;

/* ------------------------------------------------------------------------ a node */

const NODE: readonly Spec[] = [
  {
    id: 'open', group: 'open', modes: 'both',
    show: (f) => !node(f).root,
    make: (f) => item('open', 'Open', 'panel', { keys: f.editing ? 'Double-click' : 'Click' }),
  },
  {
    id: 'expand', group: 'nav', modes: 'both',
    show: (f) => !node(f).root && node(f).hasChildren,
    make: (f) =>
      node(f).collapsed ? item('expand', 'Expand', 'plus', { keys: 'Click +' }) : item('expand', 'Collapse', 'minus', { keys: 'Click –' }),
  },
  {
    id: 'expandBeneath', group: 'nav', modes: 'both',
    show: (f) => node(f).foldedBeneath,
    make: () => item('expandBeneath', 'Expand everything beneath', 'expandall'),
  },
  {
    id: 'focus', group: 'nav', modes: 'both',
    show: (f) => !node(f).root,
    make: () => item('focus', 'Collapse everything else', 'focus'),
  },
  {
    id: 'frame', group: 'nav', modes: 'both',
    show: (f) => node(f).hasChildren && !node(f).collapsed,
    make: () => item('frame', 'Fit branch to screen', 'frame'),
  },
  {
    id: 'copyLink', group: 'keep', modes: 'both',
    show: () => true,
    make: () => item('copyLink', 'Copy link', 'hash'),
  },
  {
    id: 'favourite', group: 'keep', modes: 'view',
    show: (f) => node(f).favourite !== null,
    make: (f) =>
      node(f).favourite
        ? item('favourite', 'Remove from favourites', 'star-fill')
        : item('favourite', 'Add to favourites', 'star'),
  },
  {
    id: 'follow', group: 'keep', modes: 'view',
    show: (f) => node(f).following !== null,
    make: (f) =>
      node(f).following ? item('follow', 'Stop following', 'bell-fill') : item('follow', 'Follow changes', 'bell'),
  },
  {
    id: 'tasks', group: 'work', modes: 'both',
    show: (f) => f.may.viewTodos && node(f).isProject,
    make: () => item('tasks', 'Show tasks', 'tasks'),
  },
  {
    id: 'addTask', group: 'work', modes: 'edit',
    show: (f) => f.may.viewTodos && f.may.editTodos && node(f).isProject,
    make: () => item('addTask', 'Add a task…', 'taskadd'),
  },
  {
    id: 'addChild', group: 'build', modes: 'edit',
    show: (f) => f.may.editMap,
    make: () => item('addChild', 'Add child', 'plus', { keys: 'Ctrl+click' }),
  },
  {
    id: 'addSibling', group: 'build', modes: 'edit',
    show: (f) => f.may.editMap && !node(f).root,
    make: () => item('addSibling', 'Add sibling', 'sibling'),
  },
  {
    id: 'rename', group: 'build', modes: 'edit',
    show: (f) => f.may.editMap,
    make: () => item('rename', 'Rename', 'edit'),
  },
  {
    id: 'colour', group: 'build', modes: 'edit',
    show: (f) => f.may.editMap && !node(f).root,
    make: (f) => item('colour', 'Colour', 'drop', { swatches: node(f).color }),
  },
  {
    id: 'link', group: 'build', modes: 'edit',
    show: (f) => f.may.editLinks,
    make: () => item('link', 'Link to…', 'link'),
  },
  {
    id: 'move', group: 'build', modes: 'edit',
    show: (f) => f.may.editMap && !node(f).root,
    make: () => item('move', 'Move to…', 'move', { keys: 'Drag onto a node' }),
  },
  {
    id: 'copy', group: 'clip', modes: 'edit',
    show: (f) => f.may.editMap && !node(f).root,
    make: () => item('copy', 'Copy', 'copy', { keys: 'Ctrl+C' }),
  },
  {
    id: 'copyBranch', group: 'clip', modes: 'edit',
    show: (f) => f.may.editMap && !node(f).root && node(f).hasChildren,
    make: (f) => item('copyBranch', `Copy with its branch (${node(f).descendantCount} beneath)`, 'expandall'),
  },
  {
    id: 'duplicate', group: 'clip', modes: 'edit',
    show: (f) => f.may.editMap && !node(f).root,
    make: (f) =>
      item(
        'duplicate',
        node(f).hasChildren ? `Duplicate with its branch (${node(f).descendantCount} beneath)` : 'Duplicate',
        'duplicate',
        { keys: 'Ctrl+D' },
      ),
  },
  {
    id: 'paste', group: 'clip', modes: 'edit',
    show: (f) => f.may.editMap && !!f.clipboard && node(f).pasteOk,
    make: (f) => item('paste', pasteLabel(f.clipboard!), 'paste', { keys: 'Ctrl+V' }),
  },
  {
    id: 'createDoc', group: 'docs', modes: 'edit',
    show: (f) => node(f).missingKinds.length > 0,
    make: (f) => {
      const kinds = node(f).missingKinds;
      if (kinds.length === 1) return item('createDoc', `Create the ${kinds[0].label} page`, 'docplus', { arg: kinds[0].arg });
      return item('createDoc', 'Create a page', 'docplus', {
        sub: kinds.map((k) => ({ label: k.label, arg: k.arg, dot: k.color })),
      });
    },
  },
  {
    id: 'editDoc', group: 'docs', modes: 'edit',
    show: (f) => node(f).editableDocs.length > 0,
    make: (f) => {
      const docs = node(f).editableDocs;
      if (docs.length === 1) return item('editDoc', `Edit the ${docs[0].label} page`, 'docedit', { arg: docs[0].arg });
      return item('editDoc', 'Edit a page', 'docedit', {
        sub: docs.map((d) => ({ label: d.label, arg: d.arg, dot: d.color })),
      });
    },
  },
  {
    id: 'tags', group: 'details', modes: 'edit',
    show: (f) => f.may.assignTags && !node(f).root,
    make: () => item('tags', 'Tags…', 'tag'),
  },
  {
    id: 'undo', group: 'end', modes: 'edit',
    show: (f) => !!f.undoLabel,
    make: (f) => item('undo', `Undo ${f.undoLabel}`, 'undo'),
  },
  {
    id: 'delete', group: 'end', modes: 'edit',
    show: (f) => f.may.editMap && !node(f).root,
    make: () => item('delete', 'Delete', 'trash', { danger: true }),
  },
];

/* ------------------------------------------------------------------- the empty map */

const MAP: readonly Spec[] = [
  {
    id: 'addHere', group: 'create', modes: 'edit',
    show: (f) => f.may.editMap,
    make: () => item('addHere', 'Add a node here', 'plus', { keys: 'Ctrl+click' }),
  },
  {
    id: 'pasteHere', group: 'create', modes: 'edit',
    show: (f) => f.may.editMap && !!f.clipboard,
    make: (f) => item('pasteHere', pasteLabel(f.clipboard!), 'paste', { keys: 'Ctrl+V' }),
  },
  {
    id: 'fit', group: 'view', modes: 'both',
    show: () => true,
    make: () => item('fit', 'Fit map to screen', 'target'),
  },
  {
    id: 'expandAll', group: 'branches', modes: 'both',
    show: (f) => f.map.anyFolded,
    make: () => item('expandAll', 'Expand all', 'expandall'),
  },
  {
    id: 'collapseAll', group: 'branches', modes: 'both',
    show: (f) => f.map.anyOpen,
    make: () => item('collapseAll', 'Collapse all', 'collapseall'),
  },
  {
    id: 'showLinks', group: 'show', modes: 'both',
    show: () => true,
    make: (f) => item('showLinks', 'Dotted links', 'links', { checked: f.map.showLinks }),
  },
  {
    id: 'docsPinned', group: 'show', modes: 'both',
    show: () => true,
    make: (f) => item('docsPinned', 'Documentation rows', 'doc', { checked: f.map.docsPinned }),
  },
  {
    id: 'heatmap', group: 'show', modes: 'edit',
    show: (f) => f.may.enterEdit,
    make: (f) => item('heatmap', 'Heatmap', 'heat', { checked: f.map.heatmapOn }),
  },
  {
    id: 'minimap', group: 'show', modes: 'both',
    show: () => true,
    make: (f) => item('minimap', 'Minimap', 'map', { checked: f.map.minimapOn }),
  },
  {
    id: 'clearFilters', group: 'filters', modes: 'both',
    show: (f) => f.map.filterActive,
    make: () => item('clearFilters', 'Clear search and filters', 'clear'),
  },
  {
    id: 'realign', group: 'mode', modes: 'edit',
    show: (f) => f.may.editMap && f.map.realignOk,
    make: () => item('realign', 'Realign', 'align'),
  },
  {
    id: 'editMode', group: 'mode', modes: 'both',
    show: (f) => f.may.enterEdit,
    make: (f) => item('editMode', f.editing ? 'Leave edit mode' : 'Enter edit mode', 'edit'),
  },
  {
    id: 'undo', group: 'app', modes: 'edit',
    show: (f) => !!f.undoLabel,
    make: (f) => item('undo', `Undo ${f.undoLabel}`, 'undo'),
  },
];

/* -------------------------------------------------------------------- a dotted link */

const LINK: readonly Spec[] = [
  {
    id: 'goToA', group: 'go', modes: 'both',
    show: () => true,
    make: (f) => item('goToA', `Go to ${quote(f.link!.aLabel)}`, 'arrow'),
  },
  {
    id: 'goToB', group: 'go', modes: 'both',
    show: () => true,
    make: (f) => item('goToB', `Go to ${quote(f.link!.bLabel)}`, 'arrow'),
  },
  {
    id: 'linkColour', group: 'edit', modes: 'edit',
    show: (f) => f.may.editLinks,
    make: (f) => item('linkColour', 'Colour', 'drop', { swatches: f.link!.color }),
  },
  {
    id: 'removeLink', group: 'edit', modes: 'edit',
    show: (f) => f.may.editLinks,
    make: () => item('removeLink', 'Remove link', 'unlink', { danger: true }),
  },
  {
    id: 'hideLinks', group: 'show', modes: 'both',
    show: () => true,
    make: () => item('hideLinks', 'Hide dotted links', 'eye-off'),
  },
];

/* ------------------------------------------------------------ a documentation pill */

const PILL: readonly Spec[] = [
  {
    id: 'openDoc', group: 'open', modes: 'both',
    show: () => true,
    make: () => item('openDoc', 'Open', 'doc', { keys: 'Click' }),
  },
  {
    id: 'openDocNewTab', group: 'open', modes: 'both',
    show: () => true,
    make: () => item('openDocNewTab', 'Open in a new tab', 'ext'),
  },
  {
    id: 'copyDocLink', group: 'open', modes: 'both',
    show: () => true,
    make: () => item('copyDocLink', 'Copy link', 'hash'),
  },
  {
    id: 'editDocPage', group: 'edit', modes: 'edit',
    show: (f) => !!f.pill?.canEdit,
    make: () => item('editDocPage', 'Open in the editor', 'docedit'),
  },
];

/* ---------------------------------------------------------------------- the minimap */

const MINIMAP: readonly Spec[] = [
  {
    id: 'minimapFit', group: 'minimap', modes: 'both',
    show: () => true,
    make: () => item('minimapFit', 'Fit map to screen', 'target'),
  },
  {
    id: 'minimapBright', group: 'minimap', modes: 'both',
    show: () => true,
    make: (f) => item('minimapBright', 'Keep it bright', 'sun', { checked: f.minimapBright }),
  },
  {
    id: 'minimapHide', group: 'minimap', modes: 'both',
    show: () => true,
    make: () => item('minimapHide', 'Hide minimap', 'eye-off'),
  },
];

const SPECS: Record<MenuTarget['kind'], readonly Spec[]> = {
  node: NODE,
  map: MAP,
  link: LINK,
  pill: PILL,
  minimap: MINIMAP,
};

function headerOf(target: MenuTarget, f: MenuFacts): MenuHeader | null {
  switch (target.kind) {
    case 'node':
      return f.node ? { label: f.node.label, color: f.node.color, root: f.node.root } : null;
    case 'pill':
      return f.pill ? { label: `${f.pill.kindLabel} · ${f.pill.nodeLabel}`, color: f.pill.color, root: false } : null;
    case 'link':
      return f.link ? { label: `${f.link.aLabel} ↔ ${f.link.bLabel}`, color: f.link.color, root: false } : null;
    case 'map':
      return { label: 'Map', color: null, root: false };
    case 'minimap':
      return { label: 'Minimap', color: null, root: false };
  }
}

/**
 * The menu for a right-click on `target`, in the current mode, for this person.
 *
 * `entries` may be EMPTY — a reader right-clicking a dotted link in a state where nothing
 * applies is possible in principle — and the caller then shows no menu at all rather than a
 * header over nothing. A node, pill or link whose facts are missing (it went away between the
 * click and the build) yields an empty menu for the same reason.
 */
export function buildMenu(target: MenuTarget, f: MenuFacts): BuiltMenu {
  if ((target.kind === 'node' && !f.node) || (target.kind === 'link' && !f.link) || (target.kind === 'pill' && !f.pill)) {
    return { header: null, entries: [] };
  }
  const mode: Modes = f.editing ? 'edit' : 'view';
  const entries: MenuEntry[] = [];
  let lastGroup: string | null = null;
  for (const spec of SPECS[target.kind]) {
    if (spec.modes !== 'both' && spec.modes !== mode) continue;
    if (!spec.show(f)) continue;
    if (lastGroup !== null && spec.group !== lastGroup) entries.push({ type: 'separator' });
    lastGroup = spec.group;
    entries.push(spec.make(f));
  }
  return { header: entries.length ? headerOf(target, f) : null, entries };
}

/** The item ids a menu CAN offer in a mode — the arrangement itself, for the tests to pin. */
export function offeredIds(kind: MenuTarget['kind'], editing: boolean): ItemId[] {
  const mode: Modes = editing ? 'edit' : 'view';
  return SPECS[kind].filter((s) => s.modes === 'both' || s.modes === mode).map((s) => s.id);
}

